import { LitElement, html, type TemplateResult } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { type CSECheckbox, type CSESlideIn } from '@cloudsuite/mosaic-elements'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { VariantMatrixColumnsAppliedEvent } from 'components/discovery/pdp/variant-matrix/variant-matrix-events.js'
import type { VariantMatrixColumnOption } from 'components/discovery/pdp/variant-matrix/typing/variant-matrix-typing.js'

@customElement('csc-variant-matrix-column-slide-in')
export class CSCVariantMatrixColumnSlideIn extends LitElement {
  static override styles = [hostStyle]

  @property({ type: Array })
  columns: VariantMatrixColumnOption[] = []

  @state()
  private draftColumns: VariantMatrixColumnOption[] = []

  @query('cse-slide-in')
  private slideIn!: CSESlideIn

  public open(): void {
    this.draftColumns = this.columns.map(column => ({ ...column }))
    this.slideIn.open()
  }

  public close(): void {
    this.slideIn.close()
  }

  private onColumnToggle(key: string, event: Event) {
    const checkbox = event.currentTarget as CSECheckbox
    this.draftColumns = this.draftColumns.map(column => (
      column.key === key ? { ...column, visible: checkbox.checked } : column
    ))
  }

  private onResetToDefault() {
    this.draftColumns = this.draftColumns.map(column => ({ ...column, visible: true }))
    this.dispatchEvent(new VariantMatrixColumnsAppliedEvent(this.draftColumns.map(column => column.key)))
    this.close()
  }

  private onApply() {
    const visibleColumnKeys = this.draftColumns
      .filter(column => column.visible)
      .map(column => column.key)

    this.dispatchEvent(new VariantMatrixColumnsAppliedEvent(visibleColumnKeys))
    this.close()
  }

  override render(): TemplateResult {
    return html`
      <cse-slide-in size="default">
        <div slot="header">
          ${t('columns_header', { ns: 'csc-variant-matrix-column-slide-in', default: 'Customize columns' })}
        </div>

        <cse-list>
          ${repeat(this.draftColumns, column => column.key, column => html`
            <cse-list-item>
              <cse-checkbox
                ?checked=${column.visible}
                @change=${(event: Event) => this.onColumnToggle(column.key, event)}
              >
                ${column.name}
              </cse-checkbox>
            </cse-list-item>
          `)}
        </cse-list>

        <cse-button slot="footer" fit="fill" variant="default" @click=${this.onResetToDefault}>
          ${t('reset_to_default', { ns: 'csc-variant-matrix-column-slide-in', default: 'Reset to default' })}
        </cse-button>
        <cse-button slot="footer" fit="fill" variant="primary" @click=${this.onApply}>
          ${t('apply', { ns: 'csc-variant-matrix-column-slide-in', default: 'Apply' })}
        </cse-button>
      </cse-slide-in>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-variant-matrix-column-slide-in': CSCVariantMatrixColumnSlideIn
  }
}
